import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Truck, FileText, ArrowUp, ArrowDown, Plus, Trash2, Eye, Edit } from 'lucide-react';
import CustomAlert from '../CustomAlert';
import InvoiceA4Document from './InvoiceA4Document';

const apiUrl = process.env.REACT_APP_API_URL;

const InvoiceCreateForm = forwardRef(({ existingInvoiceData = null, isEdit = false, initialDocumentType = 'invoice' }, ref) => {

  // Document type: 'invoice' (Фактура) or 'dispatch' (Испратница). The caller
  // preselects it — the Испратници list opens this screen straight on dispatch.
  const [documentType, setDocumentType] = useState(initialDocumentType);

  // Preview mode state
  const [showPreview, setShowPreview] = useState(false);
  
  // Invoice type: 'outgoing' (Излезна) or 'incoming' (Влезна)
  const [invoiceType, setInvoiceType] = useState('outgoing');
  
  const [invoice, setInvoice] = useState({
    invoiceNumber: 'INV-001',
    date: new Date().toISOString().slice(0, 10),
    fromCompanyId: null,
    fromCompanyName: '',
    toCompanyId: null,
    toCompanyName: '',
    toCompanyAddress: '',
    toCompanyPhone: '',
    items: [{ id: 1, name: '', quantity: '', unit: 'пар', price: '', total: 0 }],
    notes: '',
    currency: 'ден',
    status: 'complete'
  });

  // Transport details for dispatch (Испратница)
  const [transport, setTransport] = useState({
    dispatchAddress: '',
    vehicleRegistration: '',
    driverName: '',
    createdDate: new Date().toISOString().slice(0, 10),
    correctedDate: ''
  });

  // Related invoices for dispatch
  const [relatedInvoices, setRelatedInvoices] = useState([]);
  
  // State for adding new related invoice
  const [showAddRelatedInvoice, setShowAddRelatedInvoice] = useState(false);
  const [newRelatedInvoice, setNewRelatedInvoice] = useState({
    invoiceNumber: '',
    date: new Date().toISOString().slice(0, 10),
    customerName: '',
    items: [{ id: 1, name: '', quantity: '', unit: 'пар', price: '' }],
    notes: ''
  });

  // Business details (your companies)
  const [businessDetails, setBusinessDetails] = useState({
    id: null,
    name: '',
    currency: 'ден',
    phone: '',
    email: '',
    website: '',
    bank_name: '',
    bank_account: '',
    tax_id: '',
    address: ''
  });

  // Partner search state for "From" company
  const [fromCompanySuggestions, setFromCompanySuggestions] = useState([]);
  const [showFromCompanySuggestions, setShowFromCompanySuggestions] = useState(false);
  const [fromCompanyQuery, setFromCompanyQuery] = useState('');

  // Partner search state for "To" company
  const [toCompanySuggestions, setToCompanySuggestions] = useState([]);
  const [showToCompanySuggestions, setShowToCompanySuggestions] = useState(false);
  const [toCompanyQuery, setToCompanyQuery] = useState('');

  // Validation state
  const [validationErrors, setValidationErrors] = useState({});

  // Product search state
  const [suggestionsProduct, setSuggestionsProduct] = useState([]);
  const [productActiveRow, setProductActiveRow] = useState(null);
  const [productDropdownPos, setProductDropdownPos] = useState({ top: 0, left: 0 });

  // Alert state
  const [alert, setAlert] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    details: ''
  });

  const showAlert = (type, title, message, details = '') => {
    setAlert({ isOpen: true, type, title, message, details });
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, isOpen: false }));
  };

  // Fetch business details
  const fetchBusinessDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${apiUrl}/business-settings`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setBusinessDetails({
          id: data.id || null,
          name: data.name || '',
          currency: data.currency || 'ден',
          phone: data.phone || '',
          email: data.email || '',
          website: data.website || '',
          bank_name: data.bank_name || '',
          bank_account: data.bank_account || '',
          tax_id: data.tax_id || '',
          address: data.address || ''
        });
        setInvoice(prev => ({
          ...prev,
          currency: data.currency || 'ден'
        }));
      }
    } catch (error) {
      console.error('Failed to fetch business details:', error);
    }
  };

  // Fetch the next document number. Invoices and dispatch notes are numbered
  // in separate sequences, so the endpoint follows the selected document type.
  const fetchNextInvoiceNumber = async (type = documentType) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const endpoint = type === 'dispatch' ? 'dnote-next-number' : 'invoice-next-number';
      const response = await fetch(`${apiUrl}/${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setInvoice(prev => ({
          ...prev,
          invoiceNumber: data.nextNumber
        }));
      }
    } catch (error) {
      console.error('Failed to fetch next document number:', error);
    }
  };

  // Initial data fetch. Switching between Фактура and Испратница pulls the
  // next number from that type's own sequence.
  useEffect(() => {
    if (!isEdit) {
      fetchNextInvoiceNumber(documentType);
    }
    fetchBusinessDetails();
  }, [isEdit, documentType]);

  // Disable preview when switching to incoming invoice
  useEffect(() => {
    if (documentType === 'invoice' && invoiceType === 'incoming') {
      setShowPreview(false);
    }
    // Ispratnica is always outgoing
    if (documentType === 'dispatch') {
      setInvoiceType('outgoing');
    }
  }, [invoiceType, documentType]);

  // Load existing invoice data for edit mode
  useEffect(() => {
    if (isEdit && existingInvoiceData) {
      const { invoice: invoiceData, items } = existingInvoiceData;

      const transformedItems = items.map((item, index) => ({
        id: index + 1,
        name: item.name || item.product_name || '',
        type: item.type === 'service' ? 'service' : 'product',
        quantity: item.quantity,
        unit: item.unit || 'пар',
        price: item.price,
        total: (item.quantity || 0) * (item.price || 0)
      }));

      setInvoice(prev => ({
        ...prev,
        invoiceNumber: invoiceData.invoice_number || prev.invoiceNumber,
        date: invoiceData.date ? invoiceData.date.split('T')[0] : prev.date,
        toCompanyId: invoiceData.partner_id,
        toCompanyName: invoiceData.customer_name || '',
        toCompanyAddress: invoiceData.address || '',
        toCompanyPhone: invoiceData.phone || '',
        items: transformedItems.length > 0 ? transformedItems : prev.items,
        notes: invoiceData.note || invoiceData.notes || '',
        status: invoiceData.status || 'complete'
      }));
      // The partner field shows the query text, not toCompanyName
      setToCompanyQuery(invoiceData.customer_name || '');
    }
  }, [isEdit, existingInvoiceData]);

  // Partner search effect for "From" company
  useEffect(() => {
    const fetchFromCompanySuggestions = async () => {
      if (fromCompanyQuery.length < 1) {
        setFromCompanySuggestions([]);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${apiUrl}/partner-search?name=${encodeURIComponent(fromCompanyQuery)}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setFromCompanySuggestions(data);
        }
      } catch (error) {
        console.error('Fetch error:', error);
      }
    };

    const timeout = setTimeout(fetchFromCompanySuggestions, 100);
    return () => clearTimeout(timeout);
  }, [fromCompanyQuery]);

  // Partner search effect for "To" company
  useEffect(() => {
    const fetchToCompanySuggestions = async () => {
      if (toCompanyQuery.length < 1) {
        setToCompanySuggestions([]);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${apiUrl}/partner-search?name=${encodeURIComponent(toCompanyQuery)}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setToCompanySuggestions(data);
        }
      } catch (error) {
        console.error('Fetch error:', error);
      }
    };

    const timeout = setTimeout(fetchToCompanySuggestions, 100);
    return () => clearTimeout(timeout);
  }, [toCompanyQuery]);

  // Click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.company-selector')) {
        setShowFromCompanySuggestions(false);
        setShowToCompanySuggestions(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Fetch initial company suggestions on focus
  const fetchInitialFromCompanySuggestions = async () => {
    if (fromCompanySuggestions.length > 0) {
      setShowFromCompanySuggestions(true);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiUrl}/partners`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setFromCompanySuggestions(data);
        setShowFromCompanySuggestions(true);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    }
  };

  const fetchInitialToCompanySuggestions = async () => {
    if (toCompanySuggestions.length > 0) {
      setShowToCompanySuggestions(true);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiUrl}/partners`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setToCompanySuggestions(data.slice(0, 5));
        setShowToCompanySuggestions(true);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    }
  };

  // Calculate totals
  const calculateItemTotal = (item) => {
    const quantity = parseFloat(item.quantity || 0);
    const price = parseFloat(item.price || 0);
    return quantity * price;
  };

  const subtotal = invoice.items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
  const vatRate = 0.18;
  const vat = subtotal * vatRate;
  const total = subtotal + vat;

  // Handle item changes
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...invoice.items];
    updatedItems[index][field] = value;
    updatedItems[index].total = calculateItemTotal(updatedItems[index]);
    setInvoice({ ...invoice, items: updatedItems });
    
    if (validationErrors.items) {
      setValidationErrors(prev => ({ ...prev, items: undefined }));
    }
  };

  // Handle product selection
  const handleSelectProductAtIndex = (idx, product) => {
    const updatedItems = [...invoice.items];
    updatedItems[idx] = {
      ...updatedItems[idx],
      // Kept so a dispatch note links to this exact product instead of making
      // the backend re-match it by name.
      product_id: product.id,
      name: product.name,
      price: product.price || 0,
      quantity: updatedItems[idx].quantity || 1,
    };

    setInvoice(prev => ({ ...prev, items: updatedItems }));
    setSuggestionsProduct([]);
    setProductActiveRow(null);
  };

  // Handle product search
  const handleProductSearch = async (idx, value) => {
    setProductActiveRow(idx);
    handleItemChange(idx, 'name', value);

    // Service lines are free text — nothing to look up
    if (invoice.items[idx]?.type === 'service') {
      setSuggestionsProduct([]);
      return;
    }

    if (!value.trim()) {
      setSuggestionsProduct([]);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiUrl}/product-search?name=${encodeURIComponent(value)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch suggestions');

      const data = await res.json();
      setSuggestionsProduct(data);
      setProductActiveRow(idx);
    } catch (error) {
      console.error('Error fetching product suggestions:', error);
      setSuggestionsProduct([]);
    }
  };

  // Close product suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.product-search-container')) {
        setSuggestionsProduct([]);
        setProductActiveRow(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Add new item row
  // Switch a row between product and free-text service. A service is not
  // linked to any product, so drop a product picked earlier.
  const handleToggleItemType = (index) => {
    setInvoice(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i !== index) return item;
        const { product_id, ...rest } = item;
        return { ...rest, type: item.type === 'service' ? 'product' : 'service' };
      })
    }));
    setSuggestionsProduct([]);
  };

  const handleAddItem = (type = 'product') => {
    setInvoice(prev => ({
      ...prev,
      items: [...prev.items, { 
        id: Date.now(), 
        type,
        name: '', 
        quantity: '', 
        unit: 'пар', 
        price: '', 
        total: 0 
      }]
    }));
  };

  // Remove item row
  const handleRemoveItem = (index) => {
    if (invoice.items.length === 1) return;
    const updatedItems = invoice.items.filter((_, i) => i !== index);
    setInvoice(prev => ({ ...prev, items: updatedItems }));
  };

  // Select "From" company
  const handleSelectFromCompany = (partner) => {
    setInvoice(prev => ({
      ...prev,
      fromCompanyId: partner.id,
      fromCompanyName: partner.name
    }));
    setFromCompanyQuery(partner.name);
    setShowFromCompanySuggestions(false);
  };

  // Select "To" company
  const handleSelectToCompany = (partner) => {
    setInvoice(prev => ({
      ...prev,
      toCompanyId: partner.id,
      toCompanyName: partner.name,
      toCompanyAddress: partner.address || '',
      toCompanyPhone: partner.phone || ''
    }));
    setToCompanyQuery(partner.name);
    setShowToCompanySuggestions(false);
  };

  // Related Invoice - Add item row
  const handleAddRelatedInvoiceItem = () => {
    setNewRelatedInvoice(prev => ({
      ...prev,
      items: [...prev.items, { 
        id: Date.now(), 
        name: '', 
        quantity: '', 
        unit: 'пар', 
        price: '' 
      }]
    }));
  };

  // Related Invoice - Remove item row
  const handleRemoveRelatedInvoiceItem = (index) => {
    if (newRelatedInvoice.items.length === 1) return;
    setNewRelatedInvoice(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Related Invoice - Update item field
  const handleRelatedInvoiceItemChange = (index, field, value) => {
    setNewRelatedInvoice(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  // Related Invoice - Calculate item total
  const calculateRelatedInvoiceItemTotal = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.price) || 0;
    return qty * price;
  };

  // Related Invoice - Save and add to list
  const handleSaveRelatedInvoice = () => {
    if (!newRelatedInvoice.invoiceNumber.trim() || !newRelatedInvoice.customerName.trim()) {
      alert('Молиме внесете број на фактура и клиент');
      return;
    }

    const total = newRelatedInvoice.items.reduce((sum, item) => 
      sum + calculateRelatedInvoiceItemTotal(item), 0
    );

    setRelatedInvoices(prev => [...prev, {
      id: Date.now(),
      ...newRelatedInvoice,
      total: total.toFixed(2)
    }]);

    // Reset form
    setNewRelatedInvoice({
      invoiceNumber: '',
      date: new Date().toISOString().slice(0, 10),
      customerName: '',
      items: [{ id: 1, name: '', quantity: '', unit: 'пар', price: '' }],
      notes: ''
    });
    setShowAddRelatedInvoice(false);
  };

  // Validation
  const validateForm = () => {
    const errors = {};

    if (!invoice.invoiceNumber?.trim()) {
      errors.invoiceNumber = documentType === 'dispatch'
        ? 'Број на испратница е задолжителен'
        : 'Број на фактура е задолжителен';
    }

    // A partner typed but not picked from the list is fine — the backend
    // finds it by name or creates it.
    if (invoiceType === 'outgoing' && !invoice.toCompanyId && !toCompanyQuery.trim()) {
      errors.toCompany = documentType === 'dispatch' ? 'Изберете примач' : 'Изберете клиент';
    }

    if (invoiceType === 'incoming' && !invoice.fromCompanyId && !fromCompanyQuery.trim()) {
      errors.fromCompany = 'Изберете добавувач';
    }

    if (!invoice.date) {
      errors.date = 'Датумот е задолжителен';
    }

    const validItems = invoice.items.filter(item =>
      item.name?.trim() &&
      item.quantity &&
      parseFloat(item.quantity) > 0
    );

    if (validItems.length === 0) {
      errors.items = 'Најмалку една ставка е задолжителна';
    }

    return errors;
  };

  // Reset the form after a successful save of a new document.
  const resetAfterSave = async () => {
    setInvoice({
      invoiceNumber: '',
      date: new Date().toISOString().slice(0, 10),
      fromCompanyId: null,
      fromCompanyName: '',
      toCompanyId: null,
      toCompanyName: '',
      items: [{ id: Date.now(), name: '', quantity: '', unit: 'пар', price: '', total: 0 }],
      notes: '',
      currency: businessDetails.currency || 'ден',
      status: 'complete'
    });
    setFromCompanyQuery('');
    setToCompanyQuery('');
    await fetchNextInvoiceNumber(documentType);
  };

  // Save an Испратница. Delivery notes live in their own table, so they go to
  // /dnote-add rather than /invoice-add — the payload matches what the older
  // delivery note form sends.
  const saveDispatch = async (validItems, token, saveAsStatus) => {
    const dnoteData = {
      partner_id: invoice.toCompanyId ? Number(invoice.toCompanyId) : null,
      partner_name: toCompanyQuery.trim(),
      dnote_number: String(invoice.invoiceNumber || '').trim(),
      date: String(invoice.date || ''),
      status: String(saveAsStatus),
      note: invoice.notes || '',
      // Built from the rows themselves, not by index — empty rows are filtered
      // out before this point, so positions no longer line up with invoice.items.
      items: validItems.map((item) => {
        const dnoteItem = {
          name: String(item.name || '').trim(),
          quantity: Number(item.quantity) || 0,
          price: Number(item.price) || 0,
          note: String(item.note || '').trim(),
        };
        if (typeof item.product_id === 'number' && item.product_id > 0) {
          dnoteItem.product_id = item.product_id;
        }
        return dnoteItem;
      }),
    };

    const response = await fetch(`${apiUrl}/dnote-add`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(dnoteData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Грешка: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    showAlert(
      'success',
      'Испратница зачувана!',
      `Испратница #${dnoteData.dnote_number} е успешно зачувана!` +
        (result.partner_created ? ` Креиран е нов партнер „${dnoteData.partner_name}“.` : ''),
      `ID: ${result.id}`
    );

    await resetAfterSave();
  };

  // Save invoice
  const handleSave = async (saveAsStatus = 'complete') => {
    setValidationErrors({});

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      const validItems = invoice.items.filter(item =>
        item.name?.trim() &&
        item.quantity &&
        parseFloat(item.quantity) > 0
      );

      const sanitizedItems = validItems.map(item => ({
        name: String(item.name || '').trim(),
        quantity: Number(item.quantity) || 0,
        price: Number(item.price) || 0,
        unit: item.unit || 'пар',
        type: item.type === 'service' ? 'service' : 'product',
        tax_rate: 18
      }));

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Ве молиме најавете се за да зачувате документи.');
      }

      // Испратница is a delivery note, not an invoice — it has its own table,
      // its own number sequence and its own endpoint.
      if (documentType === 'dispatch') {
        await saveDispatch(validItems, token, saveAsStatus);
        return;
      }

      const invoiceData = {
        partner_id: invoiceType === 'outgoing' ? invoice.toCompanyId : invoice.fromCompanyId,
        partner_name: (invoiceType === 'outgoing' ? toCompanyQuery : fromCompanyQuery).trim(),
        invoice_number: String(invoice.invoiceNumber || '').trim(),
        date: String(invoice.date || ''),
        status: String(saveAsStatus),
        invoice_type: invoiceType,
        notes: invoice.notes || '',
        items: sanitizedItems
      };

      const url = isEdit && existingInvoiceData?.invoice?.id
        ? `${apiUrl}/invoice/${existingInvoiceData.invoice.id}`
        : `${apiUrl}/invoice-add`;
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(invoiceData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Грешка: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      const actionText = isEdit ? 'ажурирана' : 'зачувана';

      showAlert('success', `Фактура ${isEdit ? 'ажурирана' : 'зачувана'}!`,
        `Фактура #${invoiceData.invoice_number} е успешно ${actionText}!` +
          (result.partner_created ? ` Креиран е нов партнер „${invoiceData.partner_name}“.` : ''),
        `ID: ${result.id || existingInvoiceData?.invoice?.id}`
      );

      if (!isEdit) {
        await resetAfterSave();
      }

    } catch (error) {
      console.error('Error saving invoice:', error);
      showAlert('error', 'Грешка при зачувување', error.message);
    }
  };

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    handleSave,
  }));

  // Shared field spec, same as the add/edit drawers — this screen sits in the
  // same app and should not look like a different one.
  const LABEL = 'block text-sm font-medium text-gray-700 mb-2';
  const INPUT =
    'w-full border rounded-md px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-green-600';
  const inputClass = (hasError) =>
    `${INPUT} ${hasError ? 'border-red-500 bg-red-50' : 'border-gray-300'}`;

  const previewDisabled = documentType === 'invoice' && invoiceType === 'incoming';

  return (
    <div className="bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-end gap-3">
          <div className="flex items-center space-x-3">
            {showPreview ? (
              <button
                onClick={() => setShowPreview(false)}
                className="flex items-center space-x-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
              >
                <Edit className="w-4 h-4" />
                <span>Уреди</span>
              </button>
            ) : (
              <button
                onClick={() => !previewDisabled && setShowPreview(true)}
                disabled={previewDisabled}
                title={previewDisabled ? 'Преглед не е достапен за влезни фактури' : ''}
                className="flex items-center space-x-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors font-medium text-sm"
              >
                <Eye className="w-4 h-4" />
                <span>Преглед</span>
              </button>
            )}
            <button
              onClick={() => handleSave('complete')}
              className="px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium text-sm transition-colors"
            >
              Зачувај
            </button>
          </div>
        </div>
      </div>

      {/* Main content sits in the same max-w-7xl column as the list pages.
          overflow-x-auto keeps the A4 preview (a fixed 210mm sheet, which must
          stay that size to print correctly) scrolling inside this area instead
          of dragging the whole page sideways on a phone. */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 overflow-x-auto">
        {/* Document Type Selector (Испратница/Фактура). Hidden when editing:
            an existing invoice cannot turn into a delivery note. */}
        {!isEdit && (
        <div className="mb-6">
          <div className="flex items-center w-full border border-gray-200 rounded-lg overflow-hidden bg-white">
            {/* Испратница keeps its own colour so the two document types stay
                tellable apart at a glance; Фактура uses the app's green. */}
            <button
              onClick={() => setDocumentType('dispatch')}
              className={`flex-1 flex items-center justify-center space-x-2 px-6 py-2.5 font-medium text-sm transition-colors border-r border-gray-200 ${
                documentType === 'dispatch'
                  ? 'bg-purple-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Truck className="w-4 h-4" />
              <span>Испратница</span>
            </button>
            <button
              onClick={() => setDocumentType('invoice')}
              className={`flex-1 flex items-center justify-center space-x-2 px-6 py-2.5 font-medium text-sm transition-colors ${
                documentType === 'invoice'
                  ? 'bg-green-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <FileText className="w-4 h-4" />
              <span>Фактура</span>
            </button>
          </div>
        </div>
        )}

        {/* Preview Mode */}
        {showPreview ? (
          <InvoiceA4Document
            invoice={invoice}
            businessDetails={businessDetails}
            documentType={documentType}
            invoiceType={invoiceType}
            transport={transport}
          />
        ) : (
          <>
        {/* Basics: direction, parties, number and date — one card. No section
            heading: the page header already names the document being created. */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 mb-6 space-y-6">
        {/* Invoice Type Selector */}
        {documentType === 'invoice' && (
        <div>
          <label className={LABEL}>Тип на фактура</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
            <button
              onClick={() => setInvoiceType('outgoing')}
              className={`flex items-center justify-center p-3 sm:p-4 rounded-lg border-2 transition-all ${
                invoiceType === 'outgoing'
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-3">
                <ArrowUp className="w-5 h-5 text-green-600" />
                <div className="text-left">
                  <div className="font-medium text-gray-900">Излезна</div>
                  <div className="hidden sm:block text-sm text-gray-500">Фактура за клиент</div>
                </div>
              </div>
            </button>
            <button
              onClick={() => setInvoiceType('incoming')}
              className={`flex items-center justify-center p-3 sm:p-4 rounded-lg border-2 transition-all ${
                invoiceType === 'incoming'
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-3">
                <ArrowDown className="w-5 h-5 text-orange-600" />
                <div className="text-left">
                  <div className="font-medium text-gray-900">Влезна</div>
                  <div className="hidden sm:block text-sm text-gray-500">Фактура од добавувач</div>
                </div>
              </div>
            </button>
          </div>
        </div>
        )}

        {/* Company Selectors */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* From Company */}
          <div className={`p-4 rounded-lg border ${
            documentType === 'dispatch'
              ? 'bg-purple-50 border-purple-200'
              : invoiceType === 'outgoing'
                ? 'bg-green-50 border-green-200'
                : 'bg-white border-gray-200'
          }`}>
            <label className={LABEL}>
              {documentType === 'invoice' ? (invoiceType === 'outgoing' ? 'Од (Ваша фирма)' : 'Од (Добавувач)') : 'Од (Ваша фирма)'}
            </label>
            <div className="relative company-selector">
              {documentType === 'dispatch' || (documentType === 'invoice' && invoiceType === 'outgoing') ? (
                <div className="w-full p-3 text-gray-700 font-medium">
                  {businessDetails.name || 'Вчитување...'}
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={fromCompanyQuery}
                    onChange={(e) => {
                      setFromCompanyQuery(e.target.value);
                      // Typing replaces any partner picked earlier
                      setInvoice(prev => ({ ...prev, fromCompanyId: null, fromCompanyName: '' }));
                      setShowFromCompanySuggestions(true);
                    }}
                    onFocus={() => fetchInitialFromCompanySuggestions()}
                    placeholder="Избери или внеси нова компанија..."
                    className={inputClass(validationErrors.fromCompany)}
                  />
                  {showFromCompanySuggestions && fromCompanySuggestions.length > 0 && (
                    <ul className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-auto z-50">
                      {fromCompanySuggestions.map((partner) => (
                        <li
                          key={partner.id}
                          onClick={() => handleSelectFromCompany(partner)}
                          className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{partner.name}</div>
                          {partner.address && (
                            <div className="text-sm text-gray-500">{partner.address}</div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          </div>

          {/* To Company */}
          <div className={`p-4 rounded-lg border ${documentType === 'invoice' && invoiceType === 'incoming' ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}>
            <label className={LABEL}>
              {documentType === 'invoice' ? (invoiceType === 'outgoing' ? 'До (Клиент)' : 'До (Ваша фирма)') : 'До'}
            </label>
            <div className="relative company-selector">
              {documentType === 'invoice' && invoiceType === 'incoming' ? (
                <div className="w-full p-3 text-gray-700 font-medium">
                  {businessDetails.name || 'Вчитување...'}
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={toCompanyQuery}
                    onChange={(e) => {
                      setToCompanyQuery(e.target.value);
                      // Typing replaces any partner picked earlier
                      setInvoice(prev => ({ ...prev, toCompanyId: null, toCompanyName: '', toCompanyAddress: '', toCompanyPhone: '' }));
                      setShowToCompanySuggestions(true);
                    }}
                    onFocus={() => fetchInitialToCompanySuggestions()}
                    placeholder="Избери или внеси нова компанија..."
                    className={inputClass(validationErrors.toCompany)}
                  />
                  {showToCompanySuggestions && toCompanySuggestions.length > 0 && (
                    <ul className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-auto z-50">
                      {toCompanySuggestions.slice(0, 5).map((partner) => (
                        <li
                          key={partner.id}
                          onClick={() => handleSelectToCompany(partner)}
                          className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{partner.name}</div>
                          {partner.address && (
                            <div className="text-sm text-gray-500">{partner.address}</div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Invoice Number and Date */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={LABEL}>{documentType === 'dispatch' ? 'Број на испратница' : 'Број на фактура'}</label>
            <input
              type="text"
              value={invoice.invoiceNumber}
              onChange={(e) => {
                setInvoice({ ...invoice, invoiceNumber: e.target.value });
                if (validationErrors.invoiceNumber) {
                  setValidationErrors(prev => ({ ...prev, invoiceNumber: undefined }));
                }
              }}
              className={inputClass(validationErrors.invoiceNumber)}
              placeholder={documentType === 'dispatch' ? 'DO-001' : 'INV-001'}
            />
          </div>
          <div>
            <label className={LABEL}>Датум</label>
            <input
              type="date"
              value={invoice.date}
              onChange={(e) => setInvoice({ ...invoice, date: e.target.value })}
              className={inputClass(validationErrors.date)}
            />
          </div>
        </div>
        </div>

        {/* Transport Details - Only for Dispatch */}
        {documentType === 'dispatch' && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 mb-6">
          <div className="flex items-center space-x-2 mb-4">
            <Truck className="w-5 h-5 text-purple-600" />
            <h2 className="text-base font-semibold text-gray-800">Транспорт детали</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className={LABEL}>Адреса на испорака</label>
              <input
                type="text"
                value={transport.dispatchAddress}
                onChange={(e) => setTransport({ ...transport, dispatchAddress: e.target.value })}
                placeholder="Адреса"
                className={inputClass(false)}
              />
            </div>
            <div>
              <label className={LABEL}>Возило / Рег. број</label>
              <input
                type="text"
                value={transport.vehicleRegistration}
                onChange={(e) => setTransport({ ...transport, vehicleRegistration: e.target.value })}
                placeholder="SK-0000-AA"
                className={inputClass(false)}
              />
            </div>
            <div>
              <label className={LABEL}>Подигнал</label>
              <input
                type="text"
                value={transport.driverName}
                onChange={(e) => setTransport({ ...transport, driverName: e.target.value })}
                placeholder="Име"
                className={inputClass(false)}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div>
              <label className={LABEL}>Датум на креирање</label>
              <input
                type="date"
                value={transport.createdDate}
                onChange={(e) => setTransport({ ...transport, createdDate: e.target.value })}
                className={inputClass(false)}
              />
            </div>
            <div>
              <label className={LABEL}>Датум на испратница</label>
              <input
                type="date"
                value={transport.correctedDate}
                onChange={(e) => setTransport({ ...transport, correctedDate: e.target.value })}
                className={inputClass(false)}
              />
            </div>
          </div>
        </div>
        )}

        {/* Items Table */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6 overflow-hidden">
          {/* Table Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-gray-900">Ставки</h2>
            <div className="flex items-center gap-4">
              {documentType === 'invoice' && (
                <button
                  onClick={() => handleAddItem('service')}
                  className="flex items-center space-x-1 text-green-600 hover:text-green-700 transition-colors text-sm font-medium"
                  title="Слободна ставка — не се зачувува како производ"
                >
                  <Plus className="w-4 h-4" />
                  <span>Додади услуга</span>
                </button>
              )}
              <button
                onClick={() => handleAddItem('product')}
                className="flex items-center space-x-1 text-green-600 hover:text-green-700 transition-colors text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                <span>Додади</span>
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="w-full">
            <div className="min-w-full relative">
            <table className="w-full relative z-40">
              <thead>
                <tr className="text-gray-600 text-sm border-b border-gray-200 bg-gray-50">
                  <th className="text-center px-2 py-3 font-medium w-6">#</th>
                  <th className="text-left px-4 py-3 font-medium">Назив</th>
                  <th className="text-center px-4 py-3 font-medium w-20">Кол.</th>
                  <th className="text-center px-4 py-3 font-medium w-24">Единица</th>
                  {documentType === 'invoice' && (
                    <>
                      <th className="text-center px-4 py-3 font-medium w-24">Цена</th>
                      <th className="text-right px-4 py-3 font-medium w-28">Вкупно</th>
                    </>
                  )}
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item, index) => (
                  <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="text-center px-2 py-3 text-gray-500 text-sm w-6">{index + 1}</td>
                    <td className="px-4 py-3 relative product-search-container">
                      <div className="flex items-center gap-2">
                      {documentType === 'invoice' && (
                        <button
                          type="button"
                          onClick={() => handleToggleItemType(index)}
                          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium border transition-colors ${
                            item.type === 'service'
                              ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                              : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                          }`}
                          title={item.type === 'service'
                            ? 'Услуга: слободен опис, не се зачувува како производ. Кликни за производ.'
                            : 'Производ од листата. Кликни за слободна услуга.'}
                        >
                          {item.type === 'service' ? 'Услуга' : 'Производ'}
                        </button>
                      )}
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => {
                          handleProductSearch(index, e.target.value);
                          const rect = e.currentTarget.getBoundingClientRect();
                          setProductDropdownPos({ 
                            top: rect.bottom, 
                            left: rect.left 
                          });
                        }}
                        onFocus={(e) => {
                          setProductActiveRow(index);
                          const rect = e.currentTarget.getBoundingClientRect();
                          setProductDropdownPos({ 
                            top: rect.bottom, 
                            left: rect.left 
                          });
                        }}
                        placeholder={documentType === 'invoice' && item.type === 'service' ? 'Опис на услуга...' : 'Назив на ставка...'}
                        className="w-full bg-transparent text-gray-900 placeholder-gray-400 border-none focus:outline-none focus:ring-0"
                      />
                      </div>
                      {productActiveRow === index && suggestionsProduct.length > 0 && (
                        <ul className="fixed z-[9999] bg-white border border-gray-300 text-xs max-h-40 overflow-y-auto shadow-xl rounded" style={{ minWidth: '200px', top: `${productDropdownPos.top}px`, left: `${productDropdownPos.left}px` }}>
                          {suggestionsProduct.map((suggestion, sidx) => (
                            <li
                              key={sidx}
                              className="px-2 py-1 cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                              onClick={() => {
                                handleSelectProductAtIndex(index, suggestion);
                              }}
                            >
                              <div className="font-medium text-gray-900">{suggestion.name}</div>
                              {suggestion.price && (
                                <div className="text-gray-600">Цена: {suggestion.price}</div>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                        placeholder="0"
                        min="0"
                        className="w-full bg-transparent text-gray-900 placeholder-gray-400 border-none focus:outline-none focus:ring-0 text-center"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={item.unit}
                        onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                        className="w-full bg-transparent text-gray-600 border-none focus:outline-none focus:ring-0 text-center cursor-pointer"
                      >
                        <option value="пар" className="bg-white">пар</option>
                        <option value="ком" className="bg-white">ком</option>
                        <option value="кг" className="bg-white">кг</option>
                        <option value="л" className="bg-white">л</option>
                        <option value="м" className="bg-white">м</option>
                      </select>
                    </td>
                    {documentType === 'invoice' && (
                      <>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={item.price}
                            onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                            placeholder="0"
                            min="0"
                            step="0.01"
                            className="w-full bg-transparent text-gray-900 placeholder-gray-400 border-none focus:outline-none focus:ring-0 text-center"
                          />
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          {calculateItemTotal(item).toFixed(2)}
                        </td>
                      </>
                    )}
                    <td className="px-2 py-3">
                      <button
                        onClick={() => handleRemoveItem(index)}
                        className="text-gray-400 hover:text-red-500 transition-colors p-1"
                        title="Избриши ставка"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          {/* Totals - Only for Invoice */}
          {documentType === 'invoice' && (
            <div className="border-t border-gray-200 px-4 py-4 bg-gray-50">
              <div className="flex justify-end">
                <div className="text-right space-y-2">
                  <div className="flex justify-between space-x-8 text-gray-600 text-sm">
                    <span>Без ДДВ:</span>
                    <span>{subtotal.toFixed(2)} ден.</span>
                  </div>
                  <div className="flex justify-between space-x-8 text-gray-600 text-sm">
                    <span>ДДВ (18%):</span>
                    <span>{vat.toFixed(2)} ден.</span>
                  </div>
                  <div className="flex justify-between space-x-8 text-gray-900 font-semibold pt-2 border-t border-gray-200">
                    <span>Вкупно:</span>
                    <span>{total.toFixed(2)} ден.</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Related Invoices - Only for Dispatch */}
        {documentType === 'dispatch' && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <FileText className="w-5 h-5 text-purple-600" />
              <h2 className="text-base font-semibold text-gray-800">Поврзани фактури</h2>
            </div>
            <button
              className="text-purple-600 hover:text-purple-700 text-sm font-medium"
              onClick={() => setShowAddRelatedInvoice(!showAddRelatedInvoice)}
            >
              + Додади фактура
            </button>
          </div>

          {/* Add Related Invoice Form */}
          {showAddRelatedInvoice && (
            <div className="border-t border-gray-200 pt-6 mb-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Нова фактура</h3>
              
              {/* Invoice Number and Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={LABEL}>Број на фактура</label>
                  <input
                    type="text"
                    value={newRelatedInvoice.invoiceNumber}
                    onChange={(e) => setNewRelatedInvoice({ ...newRelatedInvoice, invoiceNumber: e.target.value })}
                    placeholder="INV-001"
                    className={inputClass(false)}
                  />
                </div>
                <div>
                  <label className={LABEL}>Датум</label>
                  <input
                    type="date"
                    value={newRelatedInvoice.date}
                    onChange={(e) => setNewRelatedInvoice({ ...newRelatedInvoice, date: e.target.value })}
                    className={inputClass(false)}
                  />
                </div>
              </div>

              {/* Customer Name */}
              <div className="mb-4">
                <label className={LABEL}>Клиент</label>
                <input
                  type="text"
                  value={newRelatedInvoice.customerName}
                  onChange={(e) => setNewRelatedInvoice({ ...newRelatedInvoice, customerName: e.target.value })}
                  placeholder="Назив на клиент..."
                  className={inputClass(false)}
                />
              </div>

              {/* Items Table */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden mb-4">
                <div className="flex justify-end p-3 border-b border-gray-200">
                  <button
                    onClick={handleAddRelatedInvoiceItem}
                    className="flex items-center space-x-1 text-gray-700 hover:text-gray-900 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Додади</span>
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-100">
                        <th className="text-left px-4 py-2 font-medium text-gray-600">#</th>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Назив</th>
                        <th className="text-center px-4 py-2 font-medium text-gray-600 w-20">Кол.</th>
                        <th className="text-center px-4 py-2 font-medium text-gray-600 w-24">Единица</th>
                        <th className="text-center px-4 py-2 font-medium text-gray-600 w-24">Цена</th>
                        <th className="text-right px-4 py-2 font-medium text-gray-600 w-28">Вкупно</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {newRelatedInvoice.items.map((item, index) => (
                        <tr key={item.id} className="border-b border-gray-100">
                          <td className="text-center px-4 py-3 text-gray-500 text-sm">{index + 1}</td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={item.name}
                              onChange={(e) => handleRelatedInvoiceItemChange(index, 'name', e.target.value)}
                              placeholder="Назив на ставка..."
                              className="w-full bg-transparent text-gray-900 placeholder-gray-400 border-none focus:outline-none focus:ring-0"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleRelatedInvoiceItemChange(index, 'quantity', e.target.value)}
                              placeholder="0"
                              min="0"
                              className="w-full bg-transparent text-gray-900 placeholder-gray-400 border-none focus:outline-none focus:ring-0 text-center"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={item.unit}
                              onChange={(e) => handleRelatedInvoiceItemChange(index, 'unit', e.target.value)}
                              className="w-full bg-transparent text-gray-600 border-none focus:outline-none focus:ring-0 text-center cursor-pointer"
                            >
                              <option value="пар">пар</option>
                              <option value="ком">ком</option>
                              <option value="кг">кг</option>
                              <option value="л">л</option>
                              <option value="м">м</option>
                            </select>
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              value={item.price}
                              onChange={(e) => handleRelatedInvoiceItemChange(index, 'price', e.target.value)}
                              placeholder="0"
                              min="0"
                              step="0.01"
                              className="w-full bg-transparent text-gray-900 placeholder-gray-400 border-none focus:outline-none focus:ring-0 text-center"
                            />
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600">
                            {calculateRelatedInvoiceItemTotal(item).toFixed(2)}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => handleRemoveRelatedInvoiceItem(index)}
                              className="text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div className="border-t border-gray-200 px-4 py-3 bg-gray-100 text-sm">
                  <div className="flex justify-end">
                    <div className="text-right space-y-1">
                      <div className="flex justify-between space-x-8 text-gray-600">
                        <span>Без ДДВ:</span>
                        <span>{newRelatedInvoice.items.reduce((sum, item) => sum + calculateRelatedInvoiceItemTotal(item), 0).toFixed(2)} ден.</span>
                      </div>
                      <div className="flex justify-between space-x-8 text-gray-600">
                        <span>ДДВ (18%):</span>
                        <span>{(newRelatedInvoice.items.reduce((sum, item) => sum + calculateRelatedInvoiceItemTotal(item), 0) * 0.18).toFixed(2)} ден.</span>
                      </div>
                      <div className="flex justify-between space-x-8 text-gray-900 font-semibold pt-2 border-t border-gray-300">
                        <span>Вкупно:</span>
                        <span>{(newRelatedInvoice.items.reduce((sum, item) => sum + calculateRelatedInvoiceItemTotal(item), 0) * 1.18).toFixed(2)} ден.</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="mb-4">
                <label className={LABEL}>Забелешки</label>
                <textarea
                  value={newRelatedInvoice.notes}
                  onChange={(e) => setNewRelatedInvoice({ ...newRelatedInvoice, notes: e.target.value })}
                  placeholder="Додатни информации за исправката..."
                  className={`${inputClass(false)} resize-none`}
                  rows="3"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowAddRelatedInvoice(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Откажи
                </button>
                <button
                  onClick={handleSaveRelatedInvoice}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-500 rounded-lg hover:bg-green-600"
                >
                  Зачувај
                </button>
              </div>
            </div>
          )}

          {/* Related Invoices List */}
          {relatedInvoices.length === 0 && !showAddRelatedInvoice ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">Нема повразани фактури. Кликни "Додади фактура" за да поврзеш.</p>
            </div>
          ) : relatedInvoices.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left px-4 py-2 font-medium text-gray-600">Број на фактура</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">Датум</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">Клиент</th>
                    <th className="text-right px-4 py-2 font-medium text-gray-600">Вкупно</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {relatedInvoices.map((inv) => (
                    <tr key={inv.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3">{inv.invoiceNumber}</td>
                      <td className="px-4 py-3">{inv.date}</td>
                      <td className="px-4 py-3">{inv.customerName}</td>
                      <td className="px-4 py-3 text-right">{inv.total} ден.</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setRelatedInvoices(relatedInvoices.filter(i => i.id !== inv.id))}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}

        {/* Error Modal Popup */}
        {Object.keys(validationErrors).filter(key => validationErrors[key]).length > 0 && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-in fade-in zoom-in-95">
              {/* Error Header */}
              <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-5">
                <div className="flex items-center justify-between space-x-3">
                  <div className="flex items-center space-x-3">
                    <div>
                      <h3 className="text-xl font-bold text-white">Грешка при валидација</h3>
                    </div>
                  </div>
                  <button
                    onClick={() => setValidationErrors({})}
                    className="text-white hover:text-red-100 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Error Content */}
              <div className="px-6 py-5">
                <p className="text-sm text-gray-600 mb-4">Ве молиме поправете ги следните полиња пред да продолжите:</p>
                <ul className="space-y-3">
                  {Object.entries(validationErrors)
                    .filter(([, message]) => message)
                    .map(([field, message]) => (
                      <li key={field} className="flex items-start space-x-3 bg-red-50 p-3 rounded-lg border-l-4 border-red-500">
        
                        <span className="text-sm text-gray-700 font-medium">{message}</span>
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Notes Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 mb-6">
          <label className={LABEL}>Забелешки</label>
          <textarea
            value={invoice.notes}
            onChange={(e) => setInvoice({ ...invoice, notes: e.target.value })}
            placeholder="Додатни информации..."
            rows={4}
            className={`${inputClass(false)} resize-none`}
          />
        </div>
          </>
        )}
      </div>

      {/* Custom Alert Modal */}
      <CustomAlert
        isOpen={alert.isOpen}
        onClose={closeAlert}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        details={alert.details}
      />
    </div>
  );
});

InvoiceCreateForm.displayName = 'InvoiceCreateForm';

export default InvoiceCreateForm;
